import React, { useState, useEffect, useCallback } from 'react';
import { Card, Form, Button, Alert } from 'react-bootstrap';
import { openAlert, openConfirm } from '../../utils/common';
import Spinner from '../UI/Spinner';
import calibrationPolicyService from '../../services/calibrationPolicyService';
import { sanitizeInput, validateNumericInput } from '../../utils/xssProtection';

const CalibrationPolicySettings = () => {
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [policy, setPolicy] = useState({
    daily_max: { value: 11 },
    total_max: { value: 33 },
    device_usable_from: { value: 22 },
  });
  const [formValues, setFormValues] = useState({
    dailyMax: 11,
    totalMax: 33,
  });
  const [validationErrors, setValidationErrors] = useState({
    dailyMax: null,
    totalMax: null,
  });

  const fetchPolicy = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const result = await calibrationPolicyService.getPolicy();
      setPolicy(result);
      setFormValues({
        dailyMax: result.daily_max?.value ?? 11,
        totalMax: result.total_max?.value ?? 33,
      });
    } catch (err) {
      const errorMessage = err.response?.data?.message || err.message || '정책 정보를 불러오는 중 오류가 발생했습니다.';
      setError(errorMessage);
      openAlert({ title: '알림', description: errorMessage });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchPolicy();
  }, [fetchPolicy]);

  const validateField = useCallback((fieldName, value) => {
    const numericValue = Number(value);
    if (!Number.isInteger(numericValue) || numericValue <= 0) {
      return '1 이상의 정수를 입력해주세요.';
    }
    if (fieldName === 'dailyMax' && (numericValue < 1 || numericValue > 50)) {
      return '하루 최대 횟수는 1~50 사이여야 합니다.';
    }
    if (fieldName === 'totalMax' && (numericValue < 1 || numericValue > 150)) {
      return '총 횟수는 1~150 사이여야 합니다.';
    }
    return null;
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    const sanitizedValue = sanitizeInput(value);
    const validationResult = validateNumericInput(sanitizedValue, {
      min: 0,
      max: 999,
      allowEmpty: false,
    });

    if (!validationResult.isValid) {
      setValidationErrors((prev) => ({ ...prev, [name]: validationResult.error }));
      return;
    }

    let numericValue = sanitizedValue.replace(/[^0-9]/g, '');
    if (!numericValue) numericValue = '0';

    const fieldError = validateField(name, numericValue);
    setValidationErrors((prev) => ({ ...prev, [name]: fieldError }));

    setFormValues({ ...formValues, [name]: numericValue });
  };

  const handleSave = async () => {
    if (validationErrors.dailyMax || validationErrors.totalMax) {
      openAlert({ title: '알림', description: '입력값을 확인해주세요.' });
      return;
    }

    const dailyMax = Number(formValues.dailyMax);
    const totalMax = Number(formValues.totalMax);

    openConfirm({
      title: '보정 횟수 정책 변경',
      description: `하루 ${dailyMax}회, 총 ${totalMax}회로 변경하시겠습니까?`,
      positive_method: async () => {
        setSaving(true);
        setError(null);
        setSuccess(null);
        try {
          const result = await calibrationPolicyService.updatePolicy(dailyMax, totalMax);
          setSuccess(result.message);
          await fetchPolicy();
        } catch (err) {
          setError(err.response?.data?.message || err.message || '정책 변경 중 오류가 발생했습니다.');
        } finally {
          setSaving(false);
        }
      },
    });
  };

  const hasChanges =
    policy.daily_max.value !== Number(formValues.dailyMax) ||
    policy.total_max.value !== Number(formValues.totalMax);
  const hasValidationErrors = validationErrors.dailyMax || validationErrors.totalMax;
  const canSave = hasChanges && !hasValidationErrors && !loading;
  const dailyMaxNum = Number(formValues.dailyMax) || 1;
  const totalMaxNum = Number(formValues.totalMax) || 1;
  const previewDays = Math.ceil(totalMaxNum / dailyMaxNum);
  const previewDeviceUsable = Math.round((totalMaxNum * 2) / 3);

  return (
    <Card>
      <Card.Header>
        <h5 className="mb-0">보정 횟수 설정</h5>
      </Card.Header>
      <Card.Body>
        <Spinner visible={loading} />

        {error && <Alert variant="danger">{error}</Alert>}
        {success && <Alert variant="success">{success}</Alert>}

        <div className="form_info_box mb-4">
          <p className="sub_desc">
            하루 최대 횟수와 총 횟수를 각각 설정합니다. 진행 일수는 자동 계산됩니다
            (예: 11회/일 × 총 33회 → 3일, 11회/일 × 총 11회 → 1일).
          </p>
        </div>

        <Form>
          <Form.Group className="mb-3" controlId="calibrationDailyMax">
            <Form.Label>하루 최대 보정 횟수</Form.Label>
            <div className="d-flex align-items-center">
              <Form.Control
                type="number"
                className={`w-25 ${validationErrors.dailyMax ? 'is-invalid' : ''}`}
                name="dailyMax"
                value={formValues.dailyMax}
                onChange={handleChange}
                min="1"
                max="50"
              />
              <span className="ms-2">회 / 일</span>
            </div>
            {validationErrors.dailyMax && (
              <div className="invalid-feedback d-block">{validationErrors.dailyMax}</div>
            )}
          </Form.Group>

          <Form.Group className="mb-3" controlId="calibrationTotalMax">
            <Form.Label>총 보정 횟수</Form.Label>
            <div className="d-flex align-items-center">
              <Form.Control
                type="number"
                className={`w-25 ${validationErrors.totalMax ? 'is-invalid' : ''}`}
                name="totalMax"
                value={formValues.totalMax}
                onChange={handleChange}
                min="1"
                max="150"
              />
              <span className="ms-2">회</span>
            </div>
            {validationErrors.totalMax && (
              <div className="invalid-feedback d-block">{validationErrors.totalMax}</div>
            )}
            <Form.Text className="text-muted">
              예상 진행 일수: {previewDays}일 · 기기 측정값 신뢰 시작: {previewDeviceUsable}회
            </Form.Text>
          </Form.Group>
        </Form>
      </Card.Body>
      <Card.Footer className="d-flex justify-content-end">
        <Button variant="primary" onClick={handleSave} disabled={!canSave}>
          {saving ? '저장 중...' : '설정 저장'}
        </Button>
      </Card.Footer>
    </Card>
  );
};

export default CalibrationPolicySettings;
